import { EventEmitter, Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NotificationsService } from '../../../notifications/services/notifications';
import {
  ApiRequestConfig,
  ApiRequestConfigInfoDefinitionMap,
  ApiRequestStandardResponse,
  API_ENDPOINTS,
  API_CONFIG,
  ApiModuleConfig,
  ApiRequestConfigCookieData,
} from '../../types';
import { createDownloadabeFileFromResponseAndSave } from '../../../utils/files';
import { decompressAndDecodeResponseData } from '../../../utils/data';
import { getRandomInt } from '../../../utils/random';
import { deleteCookie, getCookie, setCookie } from '../../../utils';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { ApiRequestBuilder } from '../../builder';
import {
  TenantInfo,
  TenantInfoWikiUrl,
} from '../../../../../../backend-core-lib/app/common-shared/account/models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  onInvalidAccessRights: EventEmitter<void> = new EventEmitter();
  sessionToken = '';
  tenant = '';
  tenantInfo?: TenantInfo;
  apiConfig: ApiModuleConfig = { apiCode: '', apiUrl: '' };
  apiEndpoints: ApiRequestConfigInfoDefinitionMap = {};
  persistOfflineRequests: Record<string, ApiRequestConfig> = {};
  sendingOfflineRequests = false;

  readonly refreshSessionNeededSubject = new Subject<void>();
  refreshSessionNeeded$ = this.refreshSessionNeededSubject.asObservable();

  readonly isOfflineModeSubject = new BehaviorSubject<boolean>(false);
  isOfflineMode$ = this.isOfflineModeSubject.asObservable();

  constructor(
    @Inject(API_CONFIG) apiConfig: ApiModuleConfig,
    @Inject(API_ENDPOINTS) apiEndpoints: ApiRequestConfigInfoDefinitionMap,
    private http: HttpClient,
    private notificationsService: NotificationsService,
  ) {
    this.apiConfig = apiConfig;
    this.apiEndpoints = apiEndpoints;
  }

  private getHeaders(
    sessionRequired = false,
    recaptchaCode = '',
    auth2FACode = '',
    cookiesData: ApiRequestConfigCookieData[] = [],
  ): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Access-Control-Max-Age': '7200',
      Pragma: 'no-cache',
      ...(cookiesData?.length
        ? { 'x-user-data': this.getCookiesDataBase64(cookiesData) }
        : {}),
      ...(sessionRequired
        ? { authorization: 'Bearer ' + this.sessionToken }
        : {}),
      ...(sessionRequired ? { 'x-tenant': this.getTenant() } : {}),

      ...(recaptchaCode ? { 'recaptcha-code': recaptchaCode } : {}),
      ...(auth2FACode ? { 'auth2fa-code': auth2FACode } : {}),
      // 'Accept-Language': this.customTranslateService.getCurrentLangShort()
    });
  }
  getCookiesDataBase64(cookiesData: ApiRequestConfigCookieData[]): string {
    const parsingResult: Record<string, string> = {};
    for (let cookieData of cookiesData) {
      const cookieValue = getCookie(cookieData.cookieId);
      if (cookieValue) {
        if (cookieData.propertyId) {
          const cookieValueObj = JSON.parse(cookieValue);
          if (
            cookieValueObj &&
            cookieValueObj[cookieData.propertyId] !== undefined &&
            cookieValueObj[cookieData.propertyId] !== null &&
            cookieValueObj[cookieData.propertyId] !== ''
          ) {
            parsingResult[cookieData.asId] =
              cookieValueObj[cookieData.propertyId];
          }
        } else {
          parsingResult[cookieData.asId] = cookieValue;
        }
      }
    }
    return btoa(JSON.stringify(parsingResult));
  }

  getSessionToken(): string {
    return this.sessionToken;
  }
  setSessionToken(sessionToken: string = ''): void {
    if (!sessionToken) {
      deleteCookie('accessToken');
    } else {
      setCookie('accessToken', sessionToken, 1 / 24); // 1 hour
    }
    this.sessionToken = sessionToken;
  }

  async setTenant(tenant: string = ''): Promise<void> {
    if (!tenant) {
      deleteCookie('tenant');
    } else {
      setCookie('tenant', tenant, 100); // 1 hour
    }
    this.tenant = tenant;
    if (tenant) {
      // get the tenant settings
      const tenantInfoRequest = new ApiRequestBuilder('getTenantInfo').build();
      try {
        const resultApi = await this.callApi(tenantInfoRequest);
        if (resultApi && resultApi.status === 'success') {
          this.tenantInfo = resultApi.data;
          console.log(this.tenantInfo);
        }
      } catch (e) {}
    } else {
      this.tenantInfo = undefined;
    }
  }
  getTenant(): string {
    console.log(this.tenant);
    return this.tenant;
  }

  setTenantInfoWikiUrls(wikiUrls: TenantInfoWikiUrl[]): void {
    if (this.tenantInfo) {
      this.tenantInfo.wikiUrls = cloneDeep(wikiUrls);
    }
  }

  getTenantInfo(): TenantInfo | undefined {
    return this.tenantInfo;
  }

  async callApiGetData<T>(
    apiRequestConfig: ApiRequestConfig,
    persistWithOfflineId?: string,
  ): Promise<T | undefined> {
    const callApiResult = await this.callApi(
      apiRequestConfig,
      persistWithOfflineId,
    );
    if (callApiResult.status === 'error') {
      return undefined;
    } else {
      return callApiResult.data;
    }
  }

  async callApi(
    apiRequestConfig: ApiRequestConfig,
    persistWithOfflineId?: string,
  ): Promise<ApiRequestStandardResponse> {
    const originalApiRequestConfig = cloneDeep(apiRequestConfig);
    const self = this;
    const body = apiRequestConfig.body ? apiRequestConfig.body : {};
    const apiEndpointInfo = this.apiEndpoints[apiRequestConfig.endpointId];
    if (!apiEndpointInfo) {
      console.error(
        `Endpoint with Id:${apiRequestConfig.endpointId} does not exist`,
      );
      return {
        status: 'error',
        data: 'ServerUnreachable',
      };
    }
    const headers = this.getHeaders(
      apiEndpointInfo.sessionRequired,
      apiRequestConfig.recaptchaCode,
      apiRequestConfig.auth2FACode,
      apiRequestConfig.cookiesData,
    );
    const method = apiEndpointInfo.method;
    const loadingNotificationId = getRandomInt(0, 123213213) + '';

    let urlApiEndpoint =
      this.apiConfig.apiUrl +
      '/' +
      apiEndpointInfo.endpoint +
      (apiEndpointInfo.endpoint.indexOf('?') >= 0 ? '&' : '?') +
      'code=' +
      this.apiConfig.apiCode +
      '&';
    if (apiRequestConfig.replaceUrl) {
      for (const replaceKey in apiRequestConfig.replaceUrl) {
        if (apiRequestConfig.replaceUrl[replaceKey] !== undefined) {
          urlApiEndpoint = urlApiEndpoint.replace(
            '{' + replaceKey + '}',
            apiRequestConfig.replaceUrl[replaceKey] + '',
          );
        }
      }
    }

    //  urlApiEndpoint += environment.APIURL + urlApiEndpoint + '?code=' +  environment.APICODE + '&';
    let promiseRequest: Promise<ApiRequestStandardResponse | undefined>;
    switch (method) {
      case 'POST':
        promiseRequest = this.http
          .post<ApiRequestStandardResponse>(urlApiEndpoint, body, { headers })
          .toPromise();
        break;
      case 'GET':
        // If value, add it as parameters like ?xxx=111&yyyy=2222, etc..
        let strParams = '';
        if (body) {
          for (const key in body) {
            if (body[key] !== undefined) {
              if (strParams !== '') {
                strParams += '&';
              }
              strParams += key + '=' + encodeURIComponent(body[key]);
            }
          }
        }
        promiseRequest = this.http
          .get<ApiRequestStandardResponse>(
            urlApiEndpoint + strParams /*(strParams ? '?' + strParams : '')*/,
            { headers },
          )
          .toPromise();
        break;
      case 'PUT':
        promiseRequest = this.http
          .put<ApiRequestStandardResponse>(urlApiEndpoint, body, { headers })
          .toPromise();
        break;
      case 'DELETE':
        promiseRequest = this.http
          .delete<ApiRequestStandardResponse>(urlApiEndpoint, { headers })
          .toPromise();
        break;
    }
    if (apiRequestConfig.options.showLoading) {
      this.notificationsService.addApiLoadingNotification(
        loadingNotificationId,
      );
    }

    return await promiseRequest
      .then((response) => {
        if (!response) {
          return {
            status: 'error',
            data: {},
          };
        }
        this.requestWithIdSuccessfully(persistWithOfflineId);
        if (response && response?.dataInfo?.type) {
          switch (response.dataInfo.type) {
            case 'file':
              createDownloadabeFileFromResponseAndSave(response);
              break;
            case 'compressedData':
              response.data = decompressAndDecodeResponseData(response);
              break;
          }
        }
        if (apiRequestConfig.options.showLoading) {
          self.notificationsService.deleteApiLoadingNotification(
            loadingNotificationId,
          );
        }
        return response;
      })
      .catch((error) => {
        if (apiRequestConfig.options.showLoading) {
          self.notificationsService.deleteApiLoadingNotification(
            loadingNotificationId,
          );
        }

        const errorCodeString = error?.error?.error || 'ServerUnreachable';
        if (errorCodeString === 'ServerUnreachable') {
          this.requestWithIdNoConnection(
            originalApiRequestConfig,
            persistWithOfflineId,
          );
        } else {
          this.requestWithIdSuccessfully(persistWithOfflineId);
        }

        if (apiRequestConfig?.options?.showError) {
          self.notificationsService.addApiErrorNotification(errorCodeString);
        }

        if (errorCodeString === 'InvalidAccessRights') {
          this.onInvalidAccessRights.emit();
        }

        return {
          status: 'error',
          data: errorCodeString,
        };
      });
  }
  requestWithIdNoConnection(
    apiRequestConfig: ApiRequestConfig,
    persistWithOfflineId?: string,
  ): void {
    if (persistWithOfflineId) {
      this.persistOfflineRequests[persistWithOfflineId] = apiRequestConfig;
    }

    // Change the offline mode
    this.isOfflineModeSubject.next(true);
  }

  requestWithIdSuccessfully(persistWithOfflineId?: string): void {
    if (persistWithOfflineId) {
      // Remove it from the list
      delete this.persistOfflineRequests[persistWithOfflineId];
    }

    const previousOfflineMode = this.isOfflineModeSubject.getValue();
    // Change the offline mode
    this.isOfflineModeSubject.next(false);

    // Start processing all pending the queue
    // One by one, NOT parallell
    this.startSendingOfflineRequests(previousOfflineMode);
  }
  async startSendingOfflineRequests(
    sendRefreshSessionNeeded = false,
  ): Promise<void> {
    if (this.sendingOfflineRequests) {
      return;
    }
    this.sendingOfflineRequests = true;
    try {
      const apiKeys = Object.keys(this.persistOfflineRequests) || [];
      for (let indexApiKey = 0; indexApiKey < apiKeys.length; indexApiKey++) {
        const apiKey = apiKeys[indexApiKey];
        const config = this.persistOfflineRequests[apiKey];
        if (config) {
          await this.callApi(config, apiKey);
        }
      }
    } catch (e) {}
    this.sendingOfflineRequests = false;
    if (sendRefreshSessionNeeded) {
      this.refreshSessionNeededSubject.next();
    }
  }
  async doExternaPutApiCall(
    url: string,
    fileMime: string,
    file: File,
  ): Promise<any> {
    try {
      const headers = new HttpHeaders({
        'Content-Type': fileMime,
      });
      await this.http.put<any>(url, file, { headers }).toPromise();
      return true;
    } catch (e) {
      return false;
    }
  }
}
